import React, { ReactNode, useCallback, useRef } from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp, faEdit, faFilter } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import './HeaderColumn.scss';
import { ChannelRole } from 'buyplan-common';
import useHasChannelRole from '../../selectors/useHasChannelRole';

const MIN_COLUMN_WIDTH = 50;

interface HeaderColumnProps {
    label: string;
    className?: string;
    onEdit?: () => void;
    onSort?: (orderDirection: 'ASC' | 'DESC') => void;
    onFilter?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
    onResize?: (newWidth: number) => void;
    isColumnSorted?: boolean;
    isColumnFiltered?: boolean;
    orderDirection?: 'ASC' | 'DESC';
    style?: React.CSSProperties;
    children?: ReactNode;
}

function HeaderColumn({
    children,
    label,
    onEdit,
    onSort,
    onFilter,
    onResize,
    className,
    isColumnSorted,
    isColumnFiltered,
    orderDirection,
    ...props
}: HeaderColumnProps) {
    const isReadOnlyUser = useHasChannelRole(ChannelRole.readOnly);
    const columnRef = useRef<HTMLDivElement>(null);

    const handleResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            if (!onResize || !columnRef.current) return;
            e.preventDefault();
            e.stopPropagation();

            const startX = e.clientX;
            const startWidth = columnRef.current.getBoundingClientRect().width;

            const onMouseMove = (moveEvent: MouseEvent) => {
                const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidth + (moveEvent.clientX - startX));
                onResize(newWidth);
            };

            const onMouseUp = () => {
                document.removeEventListener('mousemove', onMouseMove);
                document.removeEventListener('mouseup', onMouseUp);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
            };

            document.addEventListener('mousemove', onMouseMove);
            document.addEventListener('mouseup', onMouseUp);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },
        [onResize]
    );

    const shouldAlignRight = label.length > 15; // Check if the label is large and overflows
    const filterButton = onFilter ? (
        <div
            tabIndex={0}
            className={cn(
                'HeaderColumn__filterable HeaderColumn__button HeaderColumn__button-filter HeaderColumn__button--icon',
                isColumnFiltered && 'HeaderColumn__button-filter--filtered',
                shouldAlignRight && 'HeaderColumn__button-filter--align-right'
            )}
            role="button"
            title={`Filter ${label}`}
            onClick={(e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                onFilter(e);
            }}
        >
            <FontAwesomeIcon icon={faFilter as IconProp} />
        </div>
    ) : null;

    const editButton =
        onEdit && !isReadOnlyUser ? (
            <div
                tabIndex={0}
                className={cn(
                    'HeaderColumn__editable HeaderColumn__button HeaderColumn__button-edit HeaderColumn__button--icon'
                )}
                role="button"
                title={`Update ${label}`}
                onKeyDown={(e) => {
                    if (e.keyCode === 13) {
                        onEdit();
                    }
                }}
                onClick={onEdit}
            >
                <FontAwesomeIcon icon={faEdit as IconProp} />
            </div>
        ) : null;

    const sortButton = onSort ? (
        <div
            tabIndex={0}
            className={cn(
                'HeaderColumn__sortable HeaderColumn__button HeaderColumn__button-sort HeaderColumn__button--icon',
                isColumnSorted && 'HeaderColumn__button--active'
            )}
            role="button"
            title={`Sort ${label}`}
            onClick={() => {
                onSort(isColumnSorted && orderDirection === 'ASC' ? 'DESC' : 'ASC');
            }}
        >
            <FontAwesomeIcon icon={(isColumnSorted && orderDirection === 'DESC' ? faSortDown : faSortUp) as IconProp} />
        </div>
    ) : null;

    const hasButton = onFilter !== undefined || onSort !== undefined || onEdit !== undefined;

    return (
        <div ref={columnRef} className={cn(className, hasButton && 'HeaderColumn__header')} title={label} {...props}>
            {children}
            <div className="HeaderColumn__wrap">
                {sortButton}
                {label && (
                    <div className="HeaderColumn__label" title={`${label}`}>
                        {label}
                    </div>
                )}
            </div>
            {filterButton}
            {editButton}
            {onResize && (
                <div
                    className="HeaderColumn__resize-handle"
                    role="separator"
                    aria-orientation="vertical"
                    onMouseDown={handleResizeMouseDown}
                />
            )}
        </div>
    );
}

export default HeaderColumn;
